/**
 * Alert Log Modal
 * Shows recent alert emails fired for a given Error Detection rule
 * (streak, severity, recipients, send status).
 */

import React, { useState, useEffect } from 'react';
import { X, History, RefreshCw, AlertTriangle, CheckCircle2, XCircle, Mail } from 'lucide-react';
import api from '../../../services/api';

const statusBadge = (status) => {
  if (status === 'sent') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
        <CheckCircle2 className="w-3 h-3" />
        sent
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
      <XCircle className="w-3 h-3" />
      {status || 'failed'}
    </span>
  );
};

const severityBadge = (severity) => {
  const map = {
    armageddon: 'bg-red-50 text-red-700 border-red-200',
    critical: 'bg-orange-50 text-orange-700 border-orange-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    error: 'bg-purple-50 text-purple-700 border-purple-200',
  };
  const cls = map[severity] || 'bg-slate-50 text-slate-700 border-slate-200';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border ${cls}`}
    >
      {severity}
    </span>
  );
};

const AlertLogModal = ({ rule, onClose }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const response = await api.errorDetection.getAlerts({ ruleId: rule.rule_id, limit: 50 });
      setAlerts(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading alert log:', err);
      setError('Failed to load alert log.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [rule.rule_id]);

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-500">
              <History className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2
                className="text-lg font-bold text-slate-900"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Alert Log
              </h2>
              <p className="text-xs text-slate-500 truncate max-w-md">{rule.rule_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={load}
              className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <div className="flex items-center gap-2.5 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 mb-4">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {loading && alerts.length === 0 ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-14 rounded-lg border border-slate-200 bg-slate-50 animate-pulse"
                />
              ))}
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="mx-auto w-10 h-10 text-slate-300 mb-3" />
              <p className="text-sm font-medium text-slate-600">No alerts have been sent yet</p>
              <p className="text-xs text-slate-400 mt-1">
                Alerts fire after the rule has been breaching for the configured number of
                consecutive days.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2 px-2 font-semibold">Sent</th>
                    <th className="text-left py-2 px-2 font-semibold">Day</th>
                    <th className="text-left py-2 px-2 font-semibold">Severity</th>
                    <th className="text-right py-2 px-2 font-semibold">Errors</th>
                    <th className="text-left py-2 px-2 font-semibold">Recipients</th>
                    <th className="text-left py-2 px-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((a) => (
                    <tr key={a.alert_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-2 text-xs text-slate-600 whitespace-nowrap">
                        {new Date(a.sent_at).toLocaleString()}
                      </td>
                      <td className="py-2 px-2 text-xs text-slate-700 font-mono">{a.streak_day}</td>
                      <td className="py-2 px-2">{severityBadge(a.severity_level)}</td>
                      <td className="py-2 px-2 text-xs text-slate-700 font-mono text-right tabular-nums">
                        {a.error_count}
                      </td>
                      <td
                        className="py-2 px-2 text-xs text-slate-600 truncate max-w-xs"
                        title={a.recipients}
                      >
                        {a.recipients}
                      </td>
                      <td className="py-2 px-2">
                        <div className="flex flex-col gap-0.5">
                          {statusBadge(a.status)}
                          {a.error_message && (
                            <span
                              className="text-[10px] text-red-600 truncate max-w-xs"
                              title={a.error_message}
                            >
                              {a.error_message}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-3 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertLogModal;
